import { useCallback, useEffect, useMemo, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import type { EditorActions } from '@atlaskit/editor-core';
import {
	type UpdateField,
	updateNodeParameters,
	type UseExtensionParametersAndActionsProps,
} from '@atlassian/company-hub-common';

import { DEFAULT_STYLES } from '../../index';
import {
	ITEM,
	type ItemType,
	type LinkItemType,
	type LinksMenuParameters,
	type MenuItemType,
	type Styles,
} from '../../types';
import {
	addItemInPlaceHelper,
	deleteItemInPlaceHelper,
	updateItemInPlaceHelper,
} from '../../utils/item-tree-helpers';

const MAXIMUM_ITEM_LIMIT = 50;
const MAXIMUM_NESTING_LEVELS = 5;

// exporting functions for optimum mocking in other test files
export const getMaximumItemLimit = (): number => MAXIMUM_ITEM_LIMIT;
export const getMaximumNestingLevelLimit = (): number => MAXIMUM_NESTING_LEVELS;

export const getItemTreeCount = (items: LinksMenuParameters['items']): number => {
	return (
		items?.reduce((acc, item) => {
			return acc + 1 + getItemTreeCount((item as MenuItemType)?.childItems);
		}, 0) || 0
	);
};

export type LinksMenuExtensionActions = {
	addItem: (item: ItemType, parentId?: string) => void;
	deleteItem: (itemId: string) => void;
	moveItem: (item: ItemType, position: number, parentId?: string) => void;
	updateAlignment: (alignment: LinksMenuParameters['styles']['alignment']) => void;
	updateBorder: (border: LinksMenuParameters['styles']['border']) => void;
	updateBorderColor: (borderColor: LinksMenuParameters['styles']['borderColor']) => void;
	updateCornerRadius: (cornerRadius: LinksMenuParameters['styles']['cornerRadius']) => void;
	updateCornerStyle: (cornerStyle: LinksMenuParameters['styles']['cornerStyle']) => void;
	updateFillColor: (fillColor: LinksMenuParameters['styles']['fillColor']) => void;
	updateHighlightColor: (highlightColor: LinksMenuParameters['styles']['highlightColor']) => void;
	updateItem: (item: ItemType, title?: string, link?: string) => void;
	updateTextColor: (textColor: LinksMenuParameters['styles']['textColor']) => void;
};

type LinksMenuParametersAndActions = {
	extensionActions: LinksMenuExtensionActions;
	extensionParameters: LinksMenuParameters | null;
};

const fetchLocalIdMacroParameters = (
	editorActions: EditorActions,
	localId: string,
): LinksMenuParameters | null => {
	const localIdNode = editorActions?.getNodeByLocalId?.(localId)?.toJSON();
	const params = localIdNode?.attrs?.parameters;

	// Return a copy of the params object if it exists, otherwise return null
	return params ? { ...params } : null;
};

export const createNewLinkItemWithDefaultParameters = (): LinkItemType => ({
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	itemId: uuid(),
	link: '',
	title: '',
	type: ITEM.LINK,
});

export const createNewMenuItemWithDefaultParameters = (): MenuItemType => ({
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	itemId: uuid(),
	childItems: [],
	title: '',
	type: ITEM.MENU,
});

export const getValidatedItems = (itemsToValidate: any): any[] => {
	if (!itemsToValidate || !Array.isArray(itemsToValidate)) {
		return [];
	}
	return itemsToValidate;
};

export const getValidatedStyles = (styles: Styles | undefined): Styles => {
	return {
		// use defaults to fill in any missing fields
		...DEFAULT_STYLES,
		...styles,
	};
};

export const useLinksMenuParametersAndActions = ({
	localId,
	editorActions,
}: UseExtensionParametersAndActionsProps): LinksMenuParametersAndActions => {
	const [extensionParameters, setExtensionParameters] = useState<LinksMenuParameters | null>(() =>
		fetchLocalIdMacroParameters(editorActions, localId),
	);
	const { items } = extensionParameters ?? {};

	useEffect(() => {
		const extensionNodeParams = fetchLocalIdMacroParameters(editorActions, localId);
		setExtensionParameters(extensionNodeParams);
	}, [editorActions, localId]);

	const updateExtensionParams = useCallback(
		(newExtensionParameters: UpdateField<LinksMenuParameters>) => {
			if (!extensionParameters) {
				return;
			}

			const updatedParams = {
				...extensionParameters,
				...newExtensionParameters,
			};

			// Save params to ADF and state
			updateNodeParameters(editorActions, updatedParams);
			setExtensionParameters(updatedParams);
		},
		[editorActions, extensionParameters],
	);

	const addItem = useCallback(
		(item: ItemType, parentId?: string, position?: number) => {
			const clonedItems = cloneDeep(items);
			const updatedItems = addItemInPlaceHelper(
				getValidatedItems(clonedItems),
				item,
				parentId,
				position,
			);

			updateExtensionParams({
				items: updatedItems,
			});
		},
		[items, updateExtensionParams],
	);

	const updateItem = useCallback(
		(item: ItemType, title?: string, link?: string) => {
			const newItem = cloneDeep(item);
			if (title) {
				newItem.title = title;
			}
			if (link) {
				(newItem as LinkItemType).link = link;
			}
			const clonedItems = cloneDeep(items);
			const updatedItems = updateItemInPlaceHelper(getValidatedItems(clonedItems), newItem);

			updateExtensionParams({
				items: updatedItems,
			});
		},
		[items, updateExtensionParams],
	);

	const moveItem = useCallback(
		(item: ItemType, position: number, parentId?: string) => {
			const clonedItems = cloneDeep(items);
			const itemsWithoutOldEntry = deleteItemInPlaceHelper(
				getValidatedItems(clonedItems),
				item.itemId,
			);
			const itemsWithNewEntry = addItemInPlaceHelper(
				itemsWithoutOldEntry,
				item,
				parentId,
				position,
			);

			updateExtensionParams({
				items: itemsWithNewEntry,
			});
		},
		[items, updateExtensionParams],
	);

	const deleteItem = useCallback(
		(itemId: string) => {
			const clonedItems = cloneDeep(items);
			const updatedItems = deleteItemInPlaceHelper(getValidatedItems(clonedItems), itemId);

			updateExtensionParams({
				items: updatedItems,
			});
		},
		[items, updateExtensionParams],
	);

	const createStyleUpdater = useCallback(
		<T>(updateField: keyof LinksMenuParameters['styles']) =>
			(value: T) => {
				setExtensionParameters((prevParams) => {
					if (!prevParams) {
						return null;
					}

					const newParams = {
						...prevParams,
						styles: {
							...getValidatedStyles(prevParams.styles),
							[updateField]: value,
						},
					};
					updateNodeParameters(editorActions, newParams);
					return newParams;
				});
			},
		[editorActions],
	);

	const extensionActions: LinksMenuExtensionActions = useMemo(
		() => ({
			addItem,
			updateItem,
			moveItem,
			deleteItem,
			updateAlignment: createStyleUpdater<LinksMenuParameters['styles']['alignment']>('alignment'),
			updateFillColor: createStyleUpdater<LinksMenuParameters['styles']['fillColor']>('fillColor'),
			updateBorder: createStyleUpdater<LinksMenuParameters['styles']['border']>('border'),
			updateCornerStyle:
				createStyleUpdater<LinksMenuParameters['styles']['cornerStyle']>('cornerStyle'),
			updateCornerRadius:
				createStyleUpdater<LinksMenuParameters['styles']['cornerRadius']>('cornerRadius'),
			updateBorderColor:
				createStyleUpdater<LinksMenuParameters['styles']['borderColor']>('borderColor'),
			updateTextColor: createStyleUpdater<LinksMenuParameters['styles']['textColor']>('textColor'),
			updateHighlightColor:
				createStyleUpdater<LinksMenuParameters['styles']['highlightColor']>('highlightColor'),
		}),
		[addItem, updateItem, moveItem, deleteItem, createStyleUpdater],
	);

	return {
		extensionParameters,
		extensionActions,
	};
};
